import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "../../lib/auth-client";
import { trackLoginPageViewed, identifyUser, resetIdentity } from "../../lib/analytics";
import { applyPageSeo } from "../../lib/seo";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // SEO and analytics
  useEffect(() => {
    return applyPageSeo({
      title: "Log In",
      description: "Log in to your React Email Builder account to access your templates, AI generation, and exports.",
      path: "/auth/login",
      robots: "noindex,nofollow",
    });
  }, []);

  useEffect(() => {
    trackLoginPageViewed();
  }, []);

  if (session) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const result = await authClient.signIn.email({
      email,
      password,
    });

    if (result.error) {
      setError(result.error.message || "Unable to sign in with those credentials.");
      setIsSubmitting(false);
      return;
    }

    // Identify the user in analytics
    const userId = result.data?.user?.id;
    if (userId) {
      identifyUser(userId, { email });
    }

    navigate(redirectTo);
  }

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-md border border-slate-200/80 bg-white/90 p-7 shadow-[0_24px_64px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">
            Welcome back
          </p>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
            Sign in to your account
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Pick up where you left off. Your templates and drafts are waiting.
          </p>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold text-slate-950">148+</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">Templates</p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold text-slate-950">AI</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">Powered</p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold text-slate-950">Free</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">To start</p>
          </div>
        </div>

        <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isSubmitting || sessionPending}
              required
              className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isSubmitting || sessionPending}
              required
              className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
            />
          </div>

          {error ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex items-center justify-between">
            <span />
            <Link
              to="/auth/forgot-password"
              className="text-sm font-medium text-sky-600 transition hover:text-sky-500"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || sessionPending}
            className="w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>

      <p className="mt-6 text-center text-sm text-slate-500">
        Don't have an account?{" "}
        <Link to={"/auth/signup" + (searchParams.get("redirect") ? "?redirect=" + searchParams.get("redirect") : "")} className="font-semibold text-sky-600 transition hover:text-sky-500">
          Sign up free
        </Link>
      </p>

      <p className="mt-3 text-center text-xs text-slate-400">
        By continuing, you agree to our{" "}
        <Link to="/legal/terms" className="font-medium text-slate-500 underline transition hover:text-slate-700">Terms</Link>
        {" "}and{" "}
        <Link to="/legal/privacy" className="font-medium text-slate-500 underline transition hover:text-slate-700">Privacy Policy</Link>.
      </p>
    </div>
  );
}